import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { INK } from "../../theme/tokens";
import SystemDialog from "../UIToolkit/Popups/Dialog";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";

export const minimalWeekIndexThatIsDraft = 4; //that's usually 3 weeks from the current start of the week.
export const draftWeekPeriod = 6; // 6 weeks from beginning of draft mode are considered draft weeks
export const draftModeAfterCurrentWeek = minimalWeekIndexThatIsDraft - 1; // 3 weeks after the current week are considered draft weeks
export const checkIsDraftMode = (weekIndex: number): boolean => weekIndex >= minimalWeekIndexThatIsDraft;
export const getDraftModeStart = () => {
	const start = DateTime.now().startOf("week").plus({ weeks: draftModeAfterCurrentWeek });
	return start.set({ day: 7 }); //set to sunday
};
export const getDraftModeEnd = () => {
	return getDraftModeStart().plus({ weeks: draftWeekPeriod, days: 6 });
};

type Props = {
	onPublish?: () => void;
	weekIndex: number;
};

export const DraftMode: FC<Props> = ({ onPublish, weekIndex }) => {
	const { isHospitalDemo } = useDepartmentFlags();
	const [warningDisplayed, setWarningDisplayed] = useState(false);
	const toggleWarning = useCallback(() => {
		setWarningDisplayed((prevState) => !prevState);
	}, []);
	const handlePublish = useCallback(() => {
		onPublish?.();
		toggleWarning();
	}, [onPublish, toggleWarning]);
	const isDraftMode = useMemo(() => checkIsDraftMode(weekIndex) && isHospitalDemo, [weekIndex, isHospitalDemo]);

	return isDraftMode ? (
		<Box
			data-testid="staffing-draft-mode-toggle"
			sx={{
				display: "flex",
				marginInline: 1,
				alignItems: "center",
				justifyContent: "center",
				gap: 1,
			}}
		>
			{warningDisplayed && (
				<SystemDialog
					open={warningDisplayed}
					onClose={toggleWarning}
					title={"Publish schedule"}
					body={"Are you sure you want to publish this schedule?"}
					onConfirm={handlePublish}
				/>
			)}
			<Typography
				variant={"h4"}
				sx={{
					color: INK.SECONDARY,
				}}
			>
				Draft Mode
			</Typography>
			<SystemButton data-testid="staffing-publish-button" onClick={toggleWarning}>
				Publish
			</SystemButton>
		</Box>
	) : null;
};
